/**
 * Where a task sits in the list it was opened from, and which tasks are
 * next to it.
 *
 * Nothing is stored: the list's query string, carried in the task screen's
 * URL, names the filters and the page, and the same paged query the list
 * ran is run again here, under the same key, so arriving from the list is
 * a cache hit. At a page edge the neighbour lives on the page before or
 * after, fetched only then.
 */
import { useQuery } from '@tanstack/react-query';

import { listTasksPage, type ListTasksFilters } from '@optum/api-client/endpoints/tasks';
import type { Task } from '@optum/api-client/types';

import { qk } from '../../lib/query-keys.js';
import { buildListFilters, type TasksUrlState } from './tasks-url-state.js';

export interface Neighbour {
  id: string;
  /** The page the neighbour is on, so the step can rewrite the URL's page. */
  page: number;
}

/**
 * `absent`: no list context. `moved`: the task is not on its page, which
 * a live view does when new tasks arrive above it.
 */
export type NeighbourStatus = 'absent' | 'loading' | 'moved' | 'ready';

export interface TaskNeighbours {
  status: NeighbourStatus;
  /** 1-based position in the whole list, when `ready`. */
  position: number | null;
  total: number;
  prev: Neighbour | null;
  next: Neighbour | null;
}

const NONE: TaskNeighbours = { status: 'absent', position: null, total: 0, prev: null, next: null };

/** A task screen carries list context when its query string names a page. */
export function hasListContext(search: string): boolean {
  return new URLSearchParams(search).has('page');
}

export function useTaskNeighbours(
  taskId: string,
  state: TasksUrlState,
  scopedOrgId: string | null,
  enabled: boolean,
): TaskNeighbours {
  const filtersFor = (page: number): ListTasksFilters => ({
    ...buildListFilters({ ...state, page }),
    ...(scopedOrgId ? { organization_id: scopedOrgId } : {}),
  });
  const current = filtersFor(state.page);
  const page = useQuery({
    queryKey: qk.tasks(current as Record<string, unknown>),
    queryFn: () => listTasksPage(current),
    enabled,
  });

  const items = page.data?.items ?? [];
  const total = page.data?.total ?? 0;
  const index = items.findIndex((t) => t.id === taskId);
  const offset = (state.page - 1) * state.size;
  const loaded = enabled && page.data !== undefined && index >= 0;
  const needBefore = loaded && index === 0 && state.page > 1;
  const needAfter = loaded && index === items.length - 1 && offset + items.length < total;

  const before = filtersFor(state.page - 1);
  const after = filtersFor(state.page + 1);
  const pageBefore = useQuery({
    queryKey: qk.tasks(before as Record<string, unknown>),
    queryFn: () => listTasksPage(before),
    enabled: needBefore,
  });
  const pageAfter = useQuery({
    queryKey: qk.tasks(after as Record<string, unknown>),
    queryFn: () => listTasksPage(after),
    enabled: needAfter,
  });

  if (!enabled) return NONE;
  if (page.data === undefined) return { ...NONE, status: 'loading' };
  if (index < 0) return { ...NONE, status: 'moved', total };

  const prev = neighbourOf(items[index - 1], state.page)
    ?? (needBefore ? neighbourOf(pageBefore.data?.items.at(-1), state.page - 1) : null);
  const next = neighbourOf(items[index + 1], state.page)
    ?? (needAfter ? neighbourOf(pageAfter.data?.items[0], state.page + 1) : null);
  return { status: 'ready', position: offset + index + 1, total, prev, next };
}

function neighbourOf(task: Task | undefined, page: number): Neighbour | null {
  return task ? { id: task.id, page } : null;
}
